// web/src/components/ds/DsPortalProvider.tsx — DS.4 (sprint aos-ds-skin-asoasis, spec §2 D8);
// DS.5 (sprint aos-ds-shell, D5/D6) mounts it on the real app shell.
//
// Supplies the vendored design system's `PortalContainerContext`
// (@ds/primitives/portal-container) with the `.aos-ds-root` element, so every
// portal-mounting DS piece inside it (the 10-file closure ledgered in
// vendor/venuz-design/PORTALS.md) renders WITHIN the skin's containment root
// instead of on `document.body` — where it would lose every skin role and
// stack above the whole app (D-1462 / CRM TRD §13 v10 H5).
//
// Renders its own `.aos-ds-root` wrapper (isolation: isolate, no z-index —
// web/src/index.css, D4). DS.5 resolved DS.4's open question ("keep this
// nested root or point the context at the outer one"): the provider's div IS
// the app shell root — `DashboardLayout.tsx` passes its layout classes,
// `data-testid="app-shell"` and the resize cursor through the HTML-attribute
// passthrough below, so there is exactly one `.aos-ds-root` in the real app
// and the portal container is the same element that isolates the stacking.
// The DS.4 mockup stories keep mounting it standalone (defaults unchanged).
//
// The icon family is mounted HERE, once, by composing `IconFamilyLucide`
// (D6): DS.4's inline 4-entry Lucide map is gone — the complete whole-tree
// alias table lives in ds-icon-aliases.ts and this file is not a second one.
//
// The container starts as `null` (server-render / pre-mount) and resolves to
// the real DOM node after mount, matching `usePortalContainer`'s own
// documented precedence (explicit prop > context > document.body): a piece
// rendered before the ref resolves falls back to `document.body` for that
// one paint, then re-renders into the root once the context updates.

import { useEffect, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
// Deep default import per D6 (no barrel); resolved for type-checking via the
// D6b generated declaration at web/ds-types/generated/primitives/portal-container.d.ts.
import { PortalContainerContext } from '@ds/primitives/portal-container';
import { IconFamilyLucide } from './IconFamilyLucide';

export interface DsPortalProviderProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  /** Test/story escape hatch — skip the mount-effect indirection. */
  containerOverride?: Element | null;
  /** Defaults to `ds-portal-root`; the app shell passes `app-shell`. */
  'data-testid'?: string;
}

export function DsPortalProvider({
  children,
  containerOverride,
  className = '',
  'data-testid': testId = 'ds-portal-root',
  ...rest
}: DsPortalProviderProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [container, setContainer] = useState<Element | null>(containerOverride ?? null);

  useEffect(() => {
    if (containerOverride !== undefined) return;
    setContainer(rootRef.current);
  }, [containerOverride]);

  return (
    <div ref={rootRef} className={`aos-ds-root ${className}`.trim()} data-testid={testId} {...rest}>
      <PortalContainerContext.Provider value={container}>
        <IconFamilyLucide>{children}</IconFamilyLucide>
      </PortalContainerContext.Provider>
    </div>
  );
}

export default DsPortalProvider;
